'use client';

import React, { useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Clock, Copy, Check, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import type { ProcessingResult } from '../lib/types';

interface ProcessingLogProps {
  results: ProcessingResult[];
  isProcessing: boolean;
}

const StatusIcon = ({ status }: { status: ProcessingResult['status'] }) => {
  if (status === 'success') return <CheckCircle2 size={15} className="text-success shrink-0" />;
  if (status === 'warning') return <AlertTriangle size={15} className="text-warning shrink-0" />;
  if (status === 'error') return <XCircle size={15} className="text-error shrink-0" />;
  return <Clock size={15} className="text-muted-foreground shrink-0 animate-pulse" />;
};

const StatusBadge = ({ status }: { status: ProcessingResult['status'] }) => {
  const map = {
    success: 'bg-success-bg text-success border-success-border',
    warning: 'bg-warning-bg text-warning border-warning-border',
    error: 'bg-error-bg text-error border-error-border',
    processing: 'bg-info-bg text-info border-info-border',
  };
  const labels = {
    success: 'Renomeado',
    warning: 'Aviso',
    error: 'Erro',
    processing: 'A processar',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${map[status]}`}
    >
      {labels[status]}
    </span>
  );
};

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // clipboard not available
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
      title="Copiar nome"
      aria-label="Copiar nome extraído"
    >
      {copied ? <Check size={12} className="text-success" /> : <Copy size={12} />}
    </button>
  );
}

function LogRow({ result, index }: { result: ProcessingResult; index: number }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="log-row-enter border-b border-border last:border-b-0"
      style={{ animationDelay: `${Math.min(index, 10) * 30}ms` }}
    >
      {/* Main row */}
      <div
        className={`grid grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-muted/40 transition-colors cursor-pointer ${
          result.message ? 'cursor-pointer' : ''
        }`}
        onClick={() => result.message && setExpanded((p) => !p)}
      >
        {/* Status icon */}
        <div className="col-span-1 flex justify-center">
          <StatusIcon status={result.status} />
        </div>

        {/* Original filename */}
        <div className="col-span-3 min-w-0">
          <div className="flex items-center gap-1.5">
            <FileText size={12} className="text-muted-foreground shrink-0" />
            <span
              className="text-sm text-foreground truncate font-mono text-xs"
              title={result.originalName}
            >
              {result.originalName}
            </span>
          </div>
        </div>

        {/* Arrow */}
        <div className="col-span-1 flex justify-center text-muted-foreground text-xs">→</div>

        {/* Extracted name */}
        <div className="col-span-4 min-w-0">
          {result.extractedName ? (
            <div className="flex items-center gap-1">
              <span
                className="text-sm font-semibold text-foreground truncate"
                title={result.extractedName}
              >
                {result.extractedName}
              </span>
              <CopyButton text={result.extractedName} />
            </div>
          ) : (
            <span className="text-xs text-muted-foreground italic">
              {result.status === 'processing' ? 'A extrair...' : 'Nome não encontrado'}
            </span>
          )}
        </div>

        {/* Output filename */}
        <div className="col-span-2 min-w-0 hidden lg:block">
          {result.outputName ? (
            <span
              className="text-xs font-mono text-muted-foreground truncate block"
              title={result.outputName}
            >
              {result.outputName}
            </span>
          ) : null}
        </div>

        {/* Status badge + expand */}
        <div className="col-span-1 flex items-center gap-1 justify-end">
          <StatusBadge status={result.status} />
          {result.message && (
            <span className="text-muted-foreground">
              {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </span>
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && result.message && (
        <div
          className={`px-10 py-2.5 text-xs border-t border-border ${
            result.status === 'error' ?'bg-error-bg text-error'
              : result.status === 'warning' ?'bg-warning-bg text-warning' :'bg-muted text-muted-foreground'
          }`}
        >
          <span className="font-medium">Detalhe: </span>
          {result.message}
        </div>
      )}
    </div>
  );
}

export default function ProcessingLog({ results, isProcessing }: ProcessingLogProps) {
  const [filter, setFilter] = useState<'all' | 'success' | 'warning' | 'error'>('all');

  const filtered = results.filter((r) => filter === 'all' || r.status === filter);

  const filterTabs: { key: typeof filter; label: string }[] = [
    { key: 'all', label: `Todos (${results.length})` },
    {
      key: 'success',
      label: `Sucesso (${results.filter((r) => r.status === 'success').length})`,
    },
    {
      key: 'warning',
      label: `Avisos (${results.filter((r) => r.status === 'warning').length})`,
    },
    {
      key: 'error',
      label: `Erros (${results.filter((r) => r.status === 'error').length})`,
    },
  ];

  if (results.length === 0 && !isProcessing) return null;

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-3.5 border-b border-border flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-foreground">Registo de processamento</h2>

        {/* Filter tabs */}
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {filterTabs.map((tab) => (
            <button
              key={`filter-${tab.key}`}
              onClick={() => setFilter(tab.key)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-150 ${
                filter === tab.key
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Column headers */}
      <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-muted/50 border-b border-border">
        <div className="col-span-1" />
        <div className="col-span-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Ficheiro original
        </div>
        <div className="col-span-1" />
        <div className="col-span-4 text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Nome extraído
        </div>
        <div className="col-span-2 text-xs font-medium text-muted-foreground uppercase tracking-wide hidden lg:block">
          Ficheiro criado
        </div>
        <div className="col-span-1 text-xs font-medium text-muted-foreground uppercase tracking-wide text-right">
          Estado
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-border max-h-[480px] overflow-y-auto scrollbar-thin">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <FileText size={32} className="text-muted-foreground/40 mb-3" />
            <p className="text-sm font-medium text-muted-foreground">
              {isProcessing ? 'Aguardando resultados...' : 'Nenhum resultado nesta categoria'}
            </p>
          </div>
        ) : (
          filtered.map((result, i) => (
            <LogRow key={`result-${result.id}`} result={result} index={i} />
          ))
        )}

        {isProcessing && results.length === 0 && (
          <div className="flex items-center gap-3 px-4 py-4">
            <div className="animate-pulse flex gap-3 w-full">
              <div className="h-4 w-4 bg-muted rounded-full" />
              <div className="h-4 bg-muted rounded flex-1" />
              <div className="h-4 bg-muted rounded w-24" />
            </div>
          </div>
        )}
      </div>

      {/* Footer note */}
      {results.length > 0 && (
        <div className="px-5 py-2.5 border-t border-border bg-muted/30 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Clique numa linha com aviso ou erro para ver detalhes
          </p>
          <p className="text-xs text-muted-foreground text-tabular">
            {results.length} ficheiro{results.length !== 1 ? 's' : ''} processado{results.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
}